/* eslint-disable global-require */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import React, { ChangeEvent } from 'react';
import Link from 'next/link';
import { Label, Input, FormGroup, Col, FormFeedback, InputProps } from 'reactstrap';
import MaskedInput from 'react-text-mask';
import countries from 'i18n-iso-countries';
import PhoneInput from 'react-phone-number-input';
import { Dictionaries, Locale } from '../i18n/i18n-config';
import { getI18nUtils } from '../i18n/i18n-utils';

import usePhoneInputLabelLanguage from '../hooks/usePhoneInputLabelLanguage';

import 'react-phone-number-input/style.css';

export type InputConfig = {
  type: InputProps['type'] | 'formatted';
  value: any;
  touched: boolean;
  valid: boolean;
  minLength?: number;
  maxLength?: number;
  intlId: keyof Dictionaries;
  order: number;
  required: boolean;
  disabled?: boolean;
  options?: (keyof Dictionaries)[];
  autoComplete?: string;
  id?: string;
  placeholder?: string;
  intlValues?: {
    privacyLink: JSX.Element;
    termsLink: JSX.Element;
  };
  className?: string;
  formFeedback?: keyof Dictionaries;
  labelSuffix?: {
    text: keyof Dictionaries;
    linkText: keyof Dictionaries;
    linkHref: string;
  };
};

function CustomInput({
  config,
  dictionary,
  input,
  inputChanged,
  inputBlurred,
  labelSize,
  inputSize,
  autofocus,
  submitting,
  locale,
}: {
  config: InputConfig;
  dictionary: Dictionaries;
  input: string;
  inputChanged: (e: ChangeEvent<HTMLInputElement> | string) => void;
  inputBlurred?: (e: React.FocusEvent<HTMLElement, Element>) => void;
  inputSize?: number;
  labelSize?: number;
  autofocus?: boolean;
  submitting?: boolean;
  locale: Locale;
}): JSX.Element {
  const { formatMessage } = getI18nUtils(dictionary);
  const phoneLabelLanguage = usePhoneInputLabelLanguage();
  const {
    required,
    intlId,
    intlValues,
    maxLength,
    value,
    type,
    touched,
    valid,
    options,
    autoComplete,
    id,
    placeholder,
    className,
    formFeedback,
    labelSuffix,
  } = config;

  let currentInput: JSX.Element | null = null;

  switch (type) {
    case 'select':
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      countries.registerLocale(require(`i18n-iso-countries/langs/${locale}.json`));
      currentInput = (
        <>
          <Label for={input} className={`${required ? ' required' : ''}`} sm={labelSize || 12}>
            {formatMessage({ id: intlId })}
          </Label>
          <Col sm={inputSize || 12}>
            <Input
              type="select"
              name={input}
              valid={touched && value && valid}
              onChange={inputChanged}
              onBlur={(e) => {
                if (inputBlurred) inputBlurred(e);
              }}
              value={value}
              className={className}
            >
              {!value && <option value="" />}
              {options &&
                options
                  .sort((currentOption: string | number, nextOption: string | number) => {
                    if (input === 'country' || input === 'nationality')
                      return countries.getName(currentOption, locale) > countries.getName(nextOption, locale) ? 1 : -1;

                    return 1;
                  })
                  .map((option: string | number) => (
                    <option key={`${input}-${option}`} value={option}>
                      {input === 'country' || input === 'nationality'
                        ? countries.getName(option, locale)
                        : formatMessage({ id: option })}
                    </option>
                  ))}
            </Input>
            {!valid && formFeedback && <FormFeedback className="d-block">{formatMessage({ id: formFeedback })}</FormFeedback>}
          </Col>
        </>
      );
      break;
    case 'formatted':
      currentInput = (
        <>
          <Label sm={labelSize || 12} for={input} className={`${required ? ' required' : ''}`} key={`masked-${input}`}>
            {formatMessage({ id: intlId })}
          </Label>
          <Col sm={inputSize || 12}>
            <MaskedInput
              name={input}
              maxLength={maxLength}
              required={required}
              className={`form-control${touched && value && valid ? ' is-valid' : ''}${
                touched && !valid ? ' is-invalid' : ''
              } ${className}`}
              id={id || input}
              value={value}
              type="text"
              placeholder={placeholder}
              onChange={inputChanged}
              onBlur={(e) => {
                if (inputBlurred) inputBlurred(e);
              }}
              autoComplete={autoComplete || 'on'}
              mask={[
                /[0-9]/,
                /[0-9]/,
                '.',
                /[0-9]/,
                /[0-9]/,
                '.',
                /[0-9]/,
                /[0-9]/,
                '-',
                /[0-9]/,
                /[0-9]/,
                /[0-9]/,
                '-',
                /[0-9]/,
                /[0-9]/,
              ]}
            />
            {!valid && formFeedback && <FormFeedback className="d-block">{formatMessage({ id: formFeedback })}</FormFeedback>}
          </Col>
        </>
      );
      break;
    case 'radio':
      currentInput = (
        <FormGroup className={className}>
          <span className="d-block mb-1 mt-3 fw-semibold">
            {`${formatMessage({
              id: intlId,
            })} ${required ? '*' : ''}`}
            {labelSuffix && (
              <span className="label-suffix ms-2">
                (
                {formatMessage(
                  { id: labelSuffix.text },
                  {
                    link: (
                      <Link href={labelSuffix.linkHref} target="_blank">
                        {formatMessage({ id: labelSuffix.linkText })}
                      </Link>
                    ),
                  }
                )}
                )
              </span>
            )}
          </span>
          {options &&
            options.map((option: string | number) => (
              <Col sm={inputSize || 12} key={option}>
                <Label check className="text-muted">
                  <Input type="radio" value={option} onChange={inputChanged} name={input} className="me-2" />
                  {formatMessage({ id: option })}
                </Label>
              </Col>
            ))}
        </FormGroup>
      );

      break;
    case 'checkbox':
      currentInput = (
        <Label sm={labelSize || 12} for={input} className={`${required ? ' required' : ''}`} key={`input-${input}`} check>
          <Input
            id={input}
            name={input}
            maxLength={maxLength}
            required={required}
            value={value}
            checked={!!value}
            type="checkbox"
            valid={touched && value && valid}
            invalid={touched && !valid}
            placeholder={placeholder}
            onChange={inputChanged}
            onBlur={(e) => {
              if (inputBlurred) inputBlurred(e);
            }}
            autoComplete={autoComplete || 'on'}
            autoFocus={autofocus || false}
          />
          {formatMessage({ id: intlId }, { ...intlValues })}
        </Label>
      );
      break;
    case 'date':
      currentInput = (
        <>
          <Label sm={labelSize || 12} for={input} className={`${required ? ' required' : ''}`} key={`input-${input}`}>
            {formatMessage({ id: intlId })}
          </Label>
          <Col sm={inputSize || 12}>
            <Input
              name={input}
              maxLength={maxLength}
              required={required}
              id={input}
              value={value}
              type={type}
              valid={touched && value && valid}
              invalid={touched && !valid}
              placeholder={placeholder}
              onChange={inputChanged}
              onBlur={(e) => {
                if (inputBlurred) inputBlurred(e);
              }}
              autoComplete={autoComplete || 'on'}
              autoFocus={autofocus || false}
              className={className}
            />

            {!valid && formFeedback && <FormFeedback className="d-block">{formatMessage({ id: formFeedback })}</FormFeedback>}
          </Col>
        </>
      );

      break;
    case 'tel':
      currentInput = (
        <>
          <Label sm={labelSize || 12} for={input} className={`${required ? ' required' : ''}`} key={`input-${input}`}>
            {formatMessage({ id: intlId })}
          </Label>
          <Col sm={inputSize || 12}>
            <PhoneInput
              className={`${touched && valid ? 'is-valid' : ''}${touched && !valid ? 'is-invalid' : ''} ${
                submitting ? 'submitting' : ''
              }`}
              id={input}
              name={input}
              defaultCountry="BE"
              required={required}
              value={value}
              labels={phoneLabelLanguage}
              international
              countryCallingCodeEditable={false}
              // value is stored directly in 'e' so 'e' can be undefined
              onChange={(e) => e && inputChanged(e)}
              onBlur={(e) => {
                if (inputBlurred) inputBlurred(e);
              }}
            />
          </Col>
        </>
      );
      break;
    default:
      currentInput = (
        <>
          <Label sm={labelSize || 12} for={input} className={`${required ? ' required' : ''}`} key={`input-${input}`}>
            {formatMessage({ id: intlId })}
          </Label>
          <Col sm={inputSize || 12}>
            <>
              <Input
                name={input}
                maxLength={maxLength}
                required={required}
                id={input}
                value={value}
                type={type}
                valid={touched && value && valid}
                invalid={touched && !valid}
                placeholder={placeholder}
                onChange={inputChanged}
                onBlur={(e) => {
                  if (inputBlurred) inputBlurred(e);
                }}
                autoComplete={autoComplete || 'on'}
                autoFocus={autofocus || false}
                className={className}
              />
              {!valid && formFeedback && <FormFeedback className="d-block">{formatMessage({ id: formFeedback })}</FormFeedback>}
            </>
          </Col>
        </>
      );
      break;
  }

  return (
    <FormGroup
      className={`mt-1 ${type === 'checkbox' ? 'form-group' : ''}`}
      row={!!(labelSize && inputSize)}
      check={type === 'checkbox'}
    >
      {currentInput}
    </FormGroup>
  );
}

export default CustomInput;
